import React, { useState } from 'react';
import { Settings2, ChevronDown } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

type PromptBoxType = 'flashcard' | 'quiz' | 'summary';

interface PromptBoxProps {
  type: PromptBoxType;
  onGenerate: (selectedOption?: string) => void;
  onSettings: () => void;
  onToggleChange?: (checked: boolean) => void;
  autoGenerateEnabled?: boolean;
  isGenerating?: boolean;
}

// Keys for summary options - these stay in English for logic
const SUMMARY_OPTION_KEYS = ['executive', 'detailed', 'quick', 'custom'] as const;

const PromptCard = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`w-full rounded-2xl border border-border bg-card p-4 shadow-sm ${className}`}>
    {children}
  </div>
);

export function PromptBox({
  type,
  onGenerate,
  onSettings,
  onToggleChange,
  autoGenerateEnabled = false,
  isGenerating = false
}: PromptBoxProps) {
  const { t, isRtl } = useLanguage();
  const [selectedSummaryKey, setSelectedSummaryKey] = useState<typeof SUMMARY_OPTION_KEYS[number]>('executive');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [autoGenerate, setAutoGenerate] = useState(autoGenerateEnabled);

  // Get localized config based on type
  const getConfig = () => ({
    title: t(`promptBox.${type}.title`),
    description: t(`promptBox.${type}.description`)
  });

  const config = getConfig();

  // Get localized summary option label
  const getSummaryOptionLabel = (key: typeof SUMMARY_OPTION_KEYS[number]) => {
    return t(`promptBox.summaryOptions.${key}`);
  };

  const handleToggleChange = () => {
    const newValue = !autoGenerate;
    setAutoGenerate(newValue);
    onToggleChange?.(newValue);
  };

  const handleGenerateClick = () => {
    if (type === 'summary') {
      // Pass the English key for logic, component receives English value
      const englishLabels: Record<typeof SUMMARY_OPTION_KEYS[number], string> = {
        executive: 'Executive Summary',
        detailed: 'Detailed Summary',
        quick: 'Quick Summary',
        custom: 'Create Custom'
      };
      onGenerate(englishLabels[selectedSummaryKey]);
    } else {
      onGenerate();
    }
  };

  return (
    <div className="mx-auto w-full max-w-3xl" dir={isRtl ? "rtl" : "ltr"}>
      <div className={type === 'summary' ? 'space-y-3' : ''}>
        {/* Main Card */}
        <PromptCard>
          <div className="flex items-center justify-between gap-4">
            {/* Left: Text Content */}
            <div className="min-w-0 flex-1">
              <div className="text-base font-medium text-foreground truncate">
                {config.title}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {config.description}
              </div>
            </div>
            
            {/* Right: Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button 
                onClick={onSettings}
                aria-label={t("promptBox.settings")}
                className="flex h-9 items-center justify-center rounded-full px-3 font-medium text-muted-foreground transition hover:bg-accent hover:text-foreground"
              >
                <Settings2 className="h-4 w-4" />
              </button>
              
              {type === 'summary' ? (
                /* Split Button Group for Summary */
                <div className="flex items-center relative">
                  <button
                    onClick={handleGenerateClick}
                    disabled={isGenerating}
                    className={`inline-flex items-center justify-center whitespace-nowrap text-sm py-2 h-10 ${isRtl ? 'rounded-r-full border-l' : 'rounded-l-full border-r'} border-primary-foreground/20 bg-primary ${isRtl ? 'pr-5 pl-3' : 'pl-5 pr-3'} font-medium text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-50`}
                  >
                    <span className="truncate max-w-[120px]">
                      {isGenerating ? t("promptBox.generating") : getSummaryOptionLabel(selectedSummaryKey)}
                    </span>
                  </button>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    disabled={isGenerating}
                    className={`inline-flex items-center justify-center whitespace-nowrap text-sm py-2 h-10 ${isRtl ? 'rounded-l-full' : 'rounded-r-full'} bg-primary px-3 font-medium text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-50`}
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={dropdownOpen}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {dropdownOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setDropdownOpen(false)}
                      />
                      <div className={`absolute top-full ${isRtl ? 'left-0' : 'right-0'} mt-2 w-48 rounded-lg bg-popover shadow-lg border border-border py-1 z-20`}>
                        {SUMMARY_OPTION_KEYS.map((optionKey) => (
                          <button
                            key={optionKey}
                            onClick={() => {
                              if (optionKey === 'custom') {
                                setDropdownOpen(false);
                                onSettings();
                              } else {
                                setSelectedSummaryKey(optionKey);
                                setDropdownOpen(false);
                              }
                            }}
                            className={`w-full ${isRtl ? 'text-right' : 'text-left'} px-4 py-2 text-sm hover:bg-accent text-popover-foreground transition-colors`}
                          >
                            {getSummaryOptionLabel(optionKey)}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                /* Simple Generate Button for Flashcard/Quiz */
                <button 
                  onClick={handleGenerateClick}
                  disabled={isGenerating}
                  className="inline-flex items-center justify-center whitespace-nowrap text-sm py-2 h-10 rounded-full bg-primary px-5 font-medium text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-50"
                >
                  {isGenerating ? t("promptBox.generating") : t("promptBox.generate")}
                </button>
              )}
            </div>
          </div>
        </PromptCard>
        
        {/* Auto-generate Toggle Card (Summary only) */}
        {type === 'summary' && (
          <PromptCard>
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <div className="text-sm font-medium text-foreground">
                  {t("promptBox.autoGenerate.title")}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {t("promptBox.autoGenerate.description")}
                </div>
              </div>
              
              {/* Toggle Switch */}
              <div className="flex items-center flex-shrink-0">
                <button
                  type="button"
                  role="switch"
                  aria-checked={autoGenerate}
                  onClick={handleToggleChange}
                  className={`peer inline-flex h-4 w-8 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                    autoGenerate 
                      ? 'bg-primary' 
                      : 'bg-muted'
                  }`}
                >
                  <span
                    className={`pointer-events-none block h-3 w-3 rounded-full bg-background shadow-lg ring-0 transition-transform ${
                      autoGenerate 
                        ? (isRtl ? '-translate-x-4' : 'translate-x-4') 
                        : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          </PromptCard>
        )}
      </div>
    </div>
  );
}
